import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { of, Observable } from 'rxjs';
import { catchError, delay, map, tap } from 'rxjs/operators';
import * as jwt_decode from 'jwt-decode';
import { esNotificationController, esLoginService } from '@examsoft/haste-ui';
import { environment } from 'src/environments/environment';
import { ResourceService } from '../services/resource.service';
import { CookieService } from '../services/cookie.service';

import {
  User,
  Course,
  Timezone,
  CurrentUser,
  Institution,
  AdvancedSettings,
  TurnItInIntegrationSettings,
} from '../interfaces';
import {
  SetUser,
  GetCourses,
  InitApp,
  Logout,
  GetTimezones,
  GetCurrentUser,
  CheckOldPassword,
  UpdateStudentProfile,
  AcceptEULA,
  SwitchProfile,
} from './app.actions';

interface Student {
  name: string;
}

export interface AppStateModel {
  loginUrl: string;
  jwt: string;
  student: Student;
  user: User;
  currentUser: CurrentUser;
  courses: Record<string, Course>;
  timezones: Timezone[];
  oldPasswordValid: boolean;
}

const getDefaultState = (): AppStateModel => ({
  loginUrl: null,
  jwt: null,
  student: null,
  user: null,
  currentUser: null,
  courses: {},
  timezones: [],
  oldPasswordValid: true,
});

@State<AppStateModel>({
  name: 'app',
  defaults: getDefaultState(),
})
@Injectable()
export class AppState {
  constructor(
    private cookieService: CookieService,
    private router: Router,
    private resourceService: ResourceService,
    private store: Store
  ) {}

  @Selector([AppState])
  static canSwitchProfile(state: AppStateModel) {
    return state.user?.availableUserTypeIds?.length > 1;
  }

  @Selector()
  static getCourse(state: AppStateModel) {
    return (courseId: string) => {
      return state.courses[courseId];
    };
  }

  @Selector([AppState])
  static selectUser(state: AppStateModel) {
    return state.user;
  }

  @Selector([AppState])
  static selectCurrentUser(state: AppStateModel) {
    return state.currentUser;
  }

  @Selector([AppState])
  static eulaAcceptanceRequired(state: AppStateModel) {
    if (
      !state.user.institution.advancedSettings.turnItInIntegrationSettings ||
      state.user.institution.advancedSettings.turnItInIntegrationSettings.hasAcceptedLatestEULA
    ) {
      return false;
    } else if (
      !state.user.institution.advancedSettings.turnItInIntegrationSettings.hasAcceptedLatestEULA
    ) {
      return true;
    }
  }

  @Selector([AppState])
  static eulaURL(state: AppStateModel) {
    return state.user.institution.advancedSettings.turnItInIntegrationSettings.eulaurl;
  }

  @Selector([AppState])
  static selectJwt(state: AppStateModel) {
    return state.jwt;
  }

  @Selector()
  static institution(state: AppStateModel) {
    return state.user?.institution;
  }

  @Selector()
  static institutionSettings(state: AppStateModel) {
    return state.user?.institution?.advancedSettings;
  }

  @Selector()
  static getCourses(state: AppStateModel) {
    return Object.values(state.courses);
  }

  @Selector()
  static institutionName(state: AppStateModel) {
    return state.user?.institution.name;
  }

  @Action(InitApp)
  init(ctx: StateContext<AppStateModel>) {
    const token = localStorage.getItem('portalToken');

    if (token) {
      const decoded = jwt_decode(token);

      ctx.patchState({
        loginUrl: document.referrer,
        jwt: token,
        student: decoded,
      });
    }

    return this.store.dispatch([new SetUser()]);
  }

  @Action(SetUser)
  SetUser(ctx: StateContext<AppStateModel>) {
    return this.resourceService.getUser().pipe(
      tap((user) => {
        ctx.setState({
          ...ctx.getState(),
          user,
        });
      })
    );
  }

  @Action(GetCourses)
  GetCourses(ctx: StateContext<AppStateModel>) {
    return this.resourceService.getCourses().pipe(
      map((results) => {
        return results.reduce((accum, course) => {
          return {
            ...accum,
            [course.id]: {
              name: course.name,
              id: course.id,
              code: course.code,
              department: course.department.name,
              startDate: course.startDate,
              endDate: course.endDate,
            },
          };
        }, {});
      }),
      tap((courses) => {
        ctx.setState({
          ...ctx.getState(),
          courses,
        });
      })
    );
  }

  @Action(Logout)
  logout(ctx: StateContext<AppStateModel>) {
    // Clear localStorage so we are no longer maintaining state here.
    localStorage.clear();
    ctx.setState(getDefaultState());
    this.cookieService.clearAll();

    esLoginService.disableRedirect = true;
    esLoginService.logout().then(() => {
      window.location.href = environment.phxLogoutUrl;
    });
  }

  @Action(SwitchProfile)
  switchProfile(ctx: StateContext<AppStateModel>) {
    const userId = ctx.getState().user.id;

    return this.resourceService
      .switchToFacultyProfile(userId)
      .pipe(tap(() => (window.location.href = environment.phxUrl)));
  }

  @Action(GetTimezones)
  getTimezones(ctx: StateContext<AppStateModel>) {
    return this.resourceService.getTimezones().pipe(
      tap((timezones) => {
        ctx.patchState({
          timezones,
        });
      })
    );
  }

  @Action(GetCurrentUser)
  getCurrentUser(ctx: StateContext<AppStateModel>) {
    return this.resourceService.getCurrentUser().pipe(
      tap((currentUser) => {
        ctx.patchState({
          currentUser,
        });
      })
    );
  }

  @Action(CheckOldPassword)
  checkOldPassword(
    ctx: StateContext<AppStateModel>,
    { payload: { oldPassword } }
  ): Observable<boolean> {
    return this.resourceService.checkOldPassword(oldPassword).pipe(
      tap((res) => {
        ctx.patchState({
          oldPasswordValid: res,
        });
      })
    );
  }

  @Action(UpdateStudentProfile)
  updateStudentProfile(
    ctx: StateContext<AppStateModel>,
    { payload: { formData, studentId } }
  ): Observable<unknown> {
    return this.resourceService.updateStudentProfile(formData, studentId).pipe(
      catchError((error) => {
        esNotificationController.create({
          state: 'danger',
          messageText: 'Please retry the saving of your profile',
          position: 'top-right',
          duration: 10000,
        });
        return of(error);
      }),
      tap(() => {
        esNotificationController.create({
          state: 'success',
          messageText: 'Profile successfuly saved!',
          position: 'top-right',
          duration: 10000,
        });
      }),
      delay(500),
      tap(() => {
        this.router.navigate(['courses']);
      })
    );
  }

  @Action(AcceptEULA)
  acceptEULA(ctx: StateContext<AppStateModel>, { payload: { language } }) {
    return this.resourceService.acceptEULA(language).pipe(
      tap(() => {
        ctx.setState(
          patch<AppStateModel>({
            user: patch<User>({
              institution: patch<Institution>({
                advancedSettings: patch<AdvancedSettings>({
                  turnItInIntegrationSettings: patch<TurnItInIntegrationSettings>({
                    hasAcceptedLatestEULA: true,
                  }),
                }),
              }),
            }),
          })
        );
      })
    );
  }
}
